import {
  AppField,
  Application,
  BaseRecord,
  GrayColors,
  LinkedRecordFieldParams,
  Member,
  PermissionAccess,
  Preferences,
  RecordTerm,
  SortRule,
  Team,
  WrappedRecord,
} from "@smartsuite/types";
import { ExternalViewportInfo } from "@smartsuite/grid/lib/src/Grid.types";
import { ColumnsWidths } from "@smartsuite/grid/lib/src/types";
import {
  GridViewOpenRecordModalParams,
  GridViewSettings,
} from "@smartsuite/gridview/lib/components/GridView/GridView.types";
import { defaultGridViewSettings } from "@smartsuite/gridview/lib/components/GridView/GridView.constants";
import { useRefState } from "@smartsuite/react-ui/lib/hooks/useRefState/useRefState";
import { wrapRecordsAndPrepareToGroup } from "@smartsuite/fields-logic/lib/group";
import { sortRecords } from "@smartsuite/fields-logic/lib/sort";
import { getFieldSlug } from "@smartsuite/fields-logic/lib/utils/fields";
import { useCallback, useMemo } from "react";
import isEqual from "react-fast-compare";

import { $t } from "../../../../../../../utils/intl";
import { formsLinkedRecordGridText } from "../formsLinkedRecordGrid.text";
import {
  dedupePreserveOrder,
  emptyGroupData,
  setPrimaryAsFirstField,
  sortStructureByLabel,
} from "../linkedRecordGrid.helpers";

export interface LinkedRecordTableEditProps {
  isReadOnly: boolean | undefined;
  application: Application;
  currentMember: Member | null;
  linkedRecordField: AppField<LinkedRecordFieldParams>;
  applicationRecords: BaseRecord[];
  memberAccess: PermissionAccess;
  userPreferences: Preferences;
  activeTeams: Team[];
  recordTerm: RecordTerm;
  limitByHeight?: boolean;
  onShowAll?: () => void;
  onChangeLinkedRecordField: (field: AppField<LinkedRecordFieldParams>) => void;
}

interface LinkedRecordTableEditData {
  settings: Partial<GridViewSettings>;
  structure: AppField[];
  records: WrappedRecord[];
  visibleFields: string[];
  columnWidths: Record<string, number>;
  externalViewportInfo: ExternalViewportInfo;
  onOpenRecordModal: ((params: GridViewOpenRecordModalParams) => void) | null;
  onColumnWidthChange: (columnWidths: ColumnsWidths) => void;
}

export const useLinkedRecordTableEdit = ({
  isReadOnly,
  application,
  linkedRecordField,
  applicationRecords,
  memberAccess: _memberAccess,
  userPreferences,
  activeTeams: _activeTeams,
  recordTerm,
  limitByHeight = false,
  onChangeLinkedRecordField,
  onShowAll,
}: LinkedRecordTableEditProps): LinkedRecordTableEditData => {
  const visibleFields = useMemo(
    () => dedupePreserveOrder(linkedRecordField.params.visible_fields ?? []),
    [linkedRecordField.params.visible_fields]
  );

  const structure = useMemo(() => {
    const sorted = sortStructureByLabel(application?.structure || []);
    const ordered = setPrimaryAsFirstField(sorted);
    const seenSlugs = new Set<string>();
    return ordered.filter((field) => {
      const key = getFieldSlug(field);
      if (seenSlugs.has(key)) return false;
      seenSlugs.add(key);
      return true;
    });
  }, [application?.structure]);

  const groupData = emptyGroupData;
  const groupFieldSlugs = useMemo(() => [] as string[], []);

  const wrappedRecords = useMemo(
    () =>
      wrapRecordsAndPrepareToGroup(applicationRecords, structure, groupFieldSlugs, userPreferences),
    [applicationRecords, structure, groupFieldSlugs, userPreferences]
  );

  const sortData = useMemo<SortRule[]>(
    () => linkedRecordField.params.sort_data ?? [],
    [linkedRecordField.params.sort_data]
  );

  const records = useMemo(() => {
    const sorted = sortRecords(
      wrappedRecords,
      structure,
      sortData,
      userPreferences,
      groupData.group
    );
    const seenIds = new Set<string>();
    return sorted.filter((row) => {
      const id = String(row.id ?? row.original?.id ?? "");
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
  }, [wrappedRecords, structure, sortData, userPreferences, groupData.group]);

  const [columnWidths, columnWidthsRef, setColumnWidths] = useRefState(
    linkedRecordField.params?.column_widths ?? {}
  );

  const canAddRecordLinkedRecord = useMemo(() => {
    const isSingle = linkedRecordField.params.entries_allowed === "single";
    if (!(linkedRecordField.params.table_add_record_allowed ?? false)) {
      return false;
    }
    if (isReadOnly) {
      return false;
    }
    if (limitByHeight) {
      return false;
    }
    if (isSingle && records.length > 0) {
      return false;
    }
    return true;
  }, [
    linkedRecordField.params.entries_allowed,
    linkedRecordField.params.table_add_record_allowed,
    isReadOnly,
    limitByHeight,
    records.length,
  ]);

  const externalViewportInfo = useMemo<ExternalViewportInfo>(
    () => ({
      viewportHeight: 0,
      scrollTop: 0,
    }),
    []
  );

  const newRecordLabel = useMemo(
    () =>
      $t(formsLinkedRecordGridText.newRecordForTerm, {
        recordTerm,
      }),
    [recordTerm]
  );

  const settings = useMemo<Partial<GridViewSettings>>(() => {
    const limitRenderedRowsByHeight = limitByHeight && groupFieldSlugs.length > 0 ? 500 : undefined;
    const onAllRowsRenderedWithinHeightLimit: undefined | (() => void) =
      typeof limitRenderedRowsByHeight === "number" ? onShowAll : undefined;

    return {
      ...defaultGridViewSettings,
      enableColumnReordering: false,
      canAddField: false,
      canAddRecord: canAddRecordLinkedRecord,
      canEditFieldPermissions: false,
      canCommentRecord: false,
      contextMenu: {
        ...defaultGridViewSettings.contextMenu,
        enable: false,
        insertRecord: false,
        insertRecordAbove: false,
        insertRecordBelow: false,
      },
      isHeadColumnIconVisible: false,
      bulkActions: {
        enable: false,
        canDelete: false,
        canDuplicate: false,
      },
      columnActionMenu: {
        ...defaultGridViewSettings.columnActionMenu,
        at: "left bottom-4",
        className: "linked-record-table-column-action-menu",
        showSort: false,
        showFilter: false,
        showCollapseColumn: false,
        showGroupBy: false,
        iconSize: "M",
        iconName: "arrow-down-small",
        items: {
          settings: {
            iconSize: "S",
            iconName: "settings-thin",
          },
          duplicate: {
            iconSize: "S",
            iconName: "copy-thin",
          },
          addField: {
            iconSize: "S",
            iconName: "plus-thin",
          },
          editFieldPermissions: {
            iconSize: "S",
            iconName: "lock-thin",
          },
          asc: {
            iconSize: "S",
            iconName: "long-arrow-down",
          },
          desc: {
            iconSize: "S",
            iconName: "long-arrow-up",
          },
          filter: {
            iconSize: "S",
            iconName: "filter-thin",
          },
          groupBy: {
            iconSize: "S",
            iconName: "group-by-thin",
          },
          hide: {
            iconSize: "S",
            iconName: "eye-hide",
          },
          delete: {
            iconSize: "S",
            iconName: "trash-thin",
          },
        },
      },
      allowSelectAllCellsWithoutFocus: false,
      showFooter: false,
      enableRecordDragAndDrop: false,
      addRowSettings: {
        iconName: "plus-thin",
        iconSize: "S",
        iconColor: GrayColors["G-8"],
        canUseAddHotkeyOnlyWhenSelected: true,
        buttonText: newRecordLabel,
      },
      groupSettings: {
        canEditGroupValue: false,
        showAggregations: false,
        groupHeaderSpan: "right",
        sizeMap: {
          "start-group-top": 43,
          "start-group": 0,
          "margin-record": 0,
          "margin-group-top": 0,
          "margin-group": 0,
          "end-group": 0,
        },
        arrowIconName: "arrow-down-thin",
        arrowIconSize: "S",
      },
      limitRenderedRowsByHeight,
      onAllRowsRenderedWithinHeightLimit,
      fieldMenu: {
        hideEditFieldPermissions: true,
      },
      isFocusCellOnSelect: true,
      rowSettings: {
        iconSize: "S",
        iconName: "expand-thin",
        iconColor: GrayColors["G-8"],
      },
    };
  }, [limitByHeight, groupFieldSlugs.length, onShowAll, canAddRecordLinkedRecord, newRecordLabel]);

  const onColumnWidthChange = useCallback(
    (updatedColumnWidths: ColumnsWidths) => {
      const prev = columnWidthsRef.current ?? {};
      if (isEqual(updatedColumnWidths, prev)) {
        return;
      }
      setColumnWidths(updatedColumnWidths);
      onChangeLinkedRecordField({
        ...linkedRecordField,
        params: {
          ...linkedRecordField.params,
          column_widths: updatedColumnWidths,
        },
      });
    },
    [columnWidthsRef, linkedRecordField, onChangeLinkedRecordField, setColumnWidths]
  );

  return {
    visibleFields,
    structure,
    records,
    settings,
    columnWidths,
    externalViewportInfo,
    onColumnWidthChange,
    onOpenRecordModal: null,
  };
};
